// ══════════════════════════════════════════════════════════════════════════════
// MES OPTIONS - PAGE ADDONS PREMIUM
// Gestion des options modulaires payantes (Résumés Appels IA, Community Manager, etc.)
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PhoneCall, Share2, Mail, Sparkles, Check, X, Loader2,
  ArrowRight, Crown, Zap, Shield, Clock, TrendingUp, Bot, Edit3, Save
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapping des icônes
const ICONS = {
  'phone-call': PhoneCall,
  'share-2': Share2,
  'mail': Mail,
  'sparkles': Sparkles,
};

export default function MesOptionsPage() {
  const [addons, setAddons] = useState([]);
  const [myAddons, setMyAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  
  // Personnalisation de l'assistant
  const [assistantName, setAssistantName] = useState(localStorage.getItem('assistant_custom_name') || 'Mon Assistant');
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(assistantName);
  
  const saveAssistantName = () => {
    if (tempName.trim().length >= 2) {
      localStorage.setItem('assistant_custom_name', tempName.trim());
      setAssistantName(tempName.trim());
      setIsEditingName(false);
      toast.success(`Ton assistant s'appelle maintenant ${tempName.trim()} !`);
    } else {
      toast.error('Le nom doit faire au moins 2 caractères');
    }
  };
  
  const resetAssistantName = () => {
    localStorage.removeItem('assistant_custom_name');
    setAssistantName('Mon Assistant');
    setTempName('Mon Assistant');
    toast.success('Nom réinitialisé');
  };
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('client_token');
    
    try {
      // Charger les addons disponibles
      const availableRes = await fetch(`${API_URL}/api/addons/available`);
      const availableData = await availableRes.json();
      
      // Charger mes addons activés
      const myRes = await fetch(`${API_URL}/api/addons/my-addons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const myData = await myRes.json();
      
      setAddons(availableData.addons || []);
      setMyAddons(myData.addons || []);
    } catch (error) {
      console.error('Error fetching addons:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const activateAddon = async (addonId) => {
    setActivating(addonId);
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/addons/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          addon_id: addonId,
          billing_cycle: 'monthly'
        })
      });
      
      if (response.ok) {
        toast.success('Option activée avec succès !');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'activation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setActivating(null);
    }
  };
  
  const isAddonActive = (addonId) => {
    return myAddons.some(a => a.addon_id === addonId);
  };
  
  const AddonCard = ({ addon }) => {
    const Icon = ICONS[addon.icon] || Sparkles;
    const isActive = isAddonActive(addon.id);
    const isAvailable = addon.is_active;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`
          relative rounded-2xl border-2 p-6 transition-all duration-300
          ${isActive 
            ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50' 
            : isAvailable
            ? 'border-slate-200 bg-white hover:border-[#1E3A5F] hover:shadow-lg'
            : 'border-slate-100 bg-slate-50 opacity-60'
          }
        `}
      >
        {/* Badge Premium */}
        {addon.category === 'bundle' && (
          <div className="absolute -top-3 -right-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
            <Crown size={12} className="inline mr-1" />
            MEILLEURE OFFRE
          </div>
        )}
        
        {/* Badge Actif */}
        {isActive && (
          <div className="absolute -top-3 left-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            <Check size={12} className="inline mr-1" />
            ACTIVÉ
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className={`
            w-14 h-14 rounded-xl flex items-center justify-center
            ${isActive 
              ? 'bg-green-500 text-white' 
              : 'bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] text-white'
            }
          `}>
            <Icon size={28} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-800">{addon.name}</h3>
            <p className="text-sm text-slate-500">{addon.description}</p>
          </div>
        </div>
        
        {/* Features */}
        <ul className="space-y-2 mb-6">
          {addon.features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
              <Check size={16} className="text-green-500 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
        
        {/* Prix */}
        <div className="flex items-end justify-between mb-4">
          <div>
            <span className="text-3xl font-bold text-[#1E3A5F]">{addon.price_monthly}€</span>
            <span className="text-slate-400 text-sm">/mois</span>
          </div>
          <div className="text-right text-sm text-slate-400">
            ou {addon.price_yearly}€/an
            <span className="block text-green-600 font-medium">
              (2 mois offerts)
            </span>
          </div>
        </div>
        
        {/* Action */}
        {isActive ? (
          <Button 
            className="w-full bg-green-500 hover:bg-green-600"
            disabled
          >
            <Check size={18} className="mr-2" />
            Option active
          </Button>
        ) : isAvailable ? (
          <Button 
            className="w-full bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] hover:from-[#2d4a6f] hover:to-[#3d6a9a]"
            onClick={() => activateAddon(addon.id)}
            disabled={activating === addon.id}
          >
            {activating === addon.id ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Activation...
              </>
            ) : (
              <>
                <Zap size={18} className="mr-2" />
                Activer maintenant
              </>
            )}
          </Button>
        ) : (
          <Button 
            className="w-full"
            variant="outline"
            disabled
          >
            <Clock size={18} className="mr-2" />
            Bientôt disponible
          </Button>
        )}
      </motion.div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="mes-options-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Mes Options Premium</h1>
        <p className="text-slate-500">
          Active les fonctionnalités IA qui boostent ta productivité
        </p>
      </div>
      
      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-[#1E3A5F]">{myAddons.length}</div>
          <div className="text-sm text-slate-500">Options actives</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {myAddons.reduce((sum, a) => {
              const info = addons.find(ad => ad.id === a.addon_id);
              return sum + (info?.price_monthly || 0);
            }, 0)}€
          </div>
          <div className="text-sm text-slate-500">Abonnement/mois</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-amber-500">∞</div>
          <div className="text-sm text-slate-500">Appels IA illimités</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">24/7</div>
          <div className="text-sm text-slate-500">Support prioritaire</div>
        </div>
      </div>
      
      {/* Note : Mode démo */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center">
            <Sparkles className="text-white" size={20} />
          </div>
          <div>
            <p className="font-semibold text-slate-800">Mode Démo Activé</p>
            <p className="text-sm text-slate-600">
              Toutes les options sont accessibles gratuitement pendant la démo. 
              Les blocages et paiements seront activés ultérieurement.
            </p>
          </div>
        </div>
      </div>
      
      {/* Section Personnalisation de l'Assistant */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] rounded-xl flex items-center justify-center">
            <Bot className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Ton Super Assistant</h2>
            <p className="text-sm text-slate-500">Personnalise ton assistant IA</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 bg-slate-50 rounded-xl p-4">
          <div className="flex-1">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  placeholder="Donne un nom à ton assistant"
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent"
                  maxLength={20}
                  autoFocus
                  data-testid="assistant-name-input"
                />
                <Button 
                  onClick={saveAssistantName}
                  className="bg-[#1E40AF] hover:bg-[#1E3A5F]"
                  data-testid="save-assistant-name-btn"
                >
                  <Save size={16} className="mr-1" /> Enregistrer
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setIsEditingName(false);
                    setTempName(assistantName);
                  }}
                >
                  Annuler
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Nom de ton assistant</p>
                  <p className="text-2xl font-bold text-[#1E40AF]">{assistantName}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setIsEditingName(true)}
                    data-testid="edit-assistant-name-btn"
                  >
                    <Edit3 size={16} className="mr-1" /> Modifier
                  </Button>
                  {assistantName !== 'Mon Assistant' && (
                    <Button 
                      variant="ghost"
                      onClick={resetAssistantName}
                      className="text-slate-500"
                    >
                      Réinitialiser
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <p className="text-xs text-slate-400 mt-3">
          Ton assistant s'appellera "{assistantName}" partout dans l'application. 
          Tu peux l'appeler comme tu veux !
        </p>
      </div>
      
      {/* Grille des addons */}
      <div className="grid md:grid-cols-2 gap-6">
        {addons.map((addon) => (
          <AddonCard key={addon.id} addon={addon} />
        ))}
      </div>
      
      {/* Section avantages */}
      <div className="mt-12 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] rounded-2xl p-8 text-white">
        <h2 className="text-2xl font-bold mb-6 text-center">Pourquoi choisir nos options IA ?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
              <TrendingUp size={32} />
            </div>
            <h3 className="font-semibold mb-2">Gain de temps</h3>
            <p className="text-sm text-white/80">
              Plus de 5h/semaine économisées grâce à l'automatisation IA
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
              <Shield size={32} />
            </div>
            <h3 className="font-semibold mb-2">Traçabilité</h3>
            <p className="text-sm text-white/80">
              Tous tes échanges clients enregistrés et archivés automatiquement
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
              <Crown size={32} />
            </div>
            <h3 className="font-semibold mb-2">Image Pro</h3>
            <p className="text-sm text-white/80">
              Des comptes-rendus professionnels qui impressionnent tes clients
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
